import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Grid3X3, Thermometer, Download } from 'lucide-react';
import { mockLCAData } from '../data/mockLCAData';
import jsPDF from 'jspdf';
import useScrollIndicator from '../hooks/useScrollIndicator';
import ScrollIndicator from './ScrollIndicator';

const MatrixHeatmap = ({ selectedImpact, lcaData, expandMap, setExpandMap, visibleStages, setVisibleStages, hideValues, setHideValues }) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [showCallout, setShowCallout] = useState(false);
  const heatmapRef = useRef(null);
  const {
    containerRef: heatmapScrollContainerRef,
    indicator: heatmapScrollIndicator,
    update: updateHeatmapScrollIndicator,
  } = useScrollIndicator();
  
  // Life cycle stages for LCA - grouped and individual versions
  const groupedLifeCycleStages = [
    { id: 'a1-a3', name: 'A1-A3', description: 'Product Stage', color: 'from-blue-400 to-blue-600' },
    { id: 'a4-a5', name: 'A4-A5', description: 'Construction Stage', color: 'from-green-400 to-green-600' },
    { id: 'b1-b5', name: 'B1-B5', description: 'Use Stage', color: 'from-amber-400 to-amber-600' },
    { id: 'b6-b7', name: 'B6-B7', description: 'Operational Use', color: 'from-yellow-400 to-yellow-600' },
    { id: 'c1-c4', name: 'C1-C4', description: 'End of Life', color: 'from-orange-400 to-orange-600' },
    { id: 'd', name: 'D', description: 'Benefits', color: 'from-emerald-400 to-emerald-600' }
  ];

  const individualLifeCycleStages = [
    { id: 'a1', name: 'A1', description: 'Raw Material Supply', color: 'from-blue-300 to-blue-500' },
    { id: 'a2', name: 'A2', description: 'Transport to Manufacturer', color: 'from-blue-400 to-blue-600' },
    { id: 'a3', name: 'A3', description: 'Manufacturing', color: 'from-blue-500 to-blue-700' },
    { id: 'a4', name: 'A4', description: 'Transport to Site', color: 'from-green-300 to-green-500' },
    { id: 'a5', name: 'A5', description: 'Construction Installation', color: 'from-green-400 to-green-600' },
    { id: 'b1', name: 'B1', description: 'Use', color: 'from-amber-300 to-amber-500' },
    { id: 'b2', name: 'B2', description: 'Maintenance', color: 'from-amber-400 to-amber-600' },
    { id: 'b3', name: 'B3', description: 'Repair', color: 'from-amber-500 to-amber-700' },
    { id: 'b4', name: 'B4', description: 'Replacement', color: 'from-amber-600 to-amber-800' },
    { id: 'b5', name: 'B5', description: 'Refurbishment', color: 'from-amber-700 to-amber-900' },
    { id: 'b6', name: 'B6', description: 'Operational Energy Use', color: 'from-yellow-300 to-yellow-500' },
    { id: 'b7', name: 'B7', description: 'Operational Water Use', color: 'from-yellow-400 to-yellow-600' },
    { id: 'c1', name: 'C1', description: 'Deconstruction', color: 'from-orange-300 to-orange-500' },
    { id: 'c2', name: 'C2', description: 'Transport', color: 'from-orange-400 to-orange-600' },
    { id: 'c3', name: 'C3', description: 'Waste Processing', color: 'from-orange-500 to-orange-700' },
    { id: 'c4', name: 'C4', description: 'Disposal', color: 'from-orange-600 to-orange-800' },
    { id: 'd', name: 'D', description: 'Benefits', color: 'from-emerald-400 to-emerald-600' }
  ];

  // Use the appropriate stages based on expand toggle and visibility filter
  const getFilteredStages = () => {
    if (expandMap) {
      return individualLifeCycleStages.filter(stage => visibleStages.has(stage.id));
    }
    return groupedLifeCycleStages;
  };
  
  const lifeCycleStages = getFilteredStages();
  
  // Handle clicking outside dropdown to close it
  useEffect(() => {
    const handleClickOutside = (event) => {
      const dropdown = document.getElementById('stageDropdown');
      const button = event.target.closest('button');
      
      if (dropdown && !dropdown.contains(event.target) && !button?.contains(event.target)) {
        dropdown.classList.add('hidden');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  // Use actual data if available, fallback to mock data
  const dataSource = lcaData?.isRhinoImport ? lcaData : (lcaData || mockLCAData);
  const components = dataSource.components;
  
  const matrixData = useMemo(() => {
    // Check if we have real LCA stages data from Excel
    const hasRealLCAStages = lcaData && lcaData.lcaStagesData && Object.keys(lcaData.lcaStagesData).length > 0;
    const isRhinoImport = lcaData?.isRhinoImport;
    
    console.log('MatrixHeatmap - Data check:');
    console.log('  - lcaData exists:', !!lcaData);
    console.log('  - lcaStagesData exists:', !!(lcaData && lcaData.lcaStagesData));
    console.log('  - lcaStagesData keys:', lcaData && lcaData.lcaStagesData ? Object.keys(lcaData.lcaStagesData) : 'N/A');
    console.log('  - hasRealLCAStages:', hasRealLCAStages);
    
    if (hasRealLCAStages) {
      // Use real LCA stages data from Excel
      console.log('MatrixHeatmap - Using real LCA stages data from Excel');
      console.log('MatrixHeatmap - Full lcaStagesData:', lcaData.lcaStagesData);
      console.log('MatrixHeatmap - Available component IDs:', components.map(c => ({ id: c.id, name: c.name })));
      
      // First, collect all individual stage values to find the true maximum
      let allStageValues = [];
      
      components.forEach(component => {
        const componentStages = lcaData.lcaStagesData[component.id] || {};
        
        if (expandMap) {
          // Individual stages mode - collect all individual stage values
          const individualStages = ['A1', 'A2', 'A3', 'A4', 'A5', 'B1', 'B2', 'B3', 'B4', 'B5', 'B6', 'B7', 'C1', 'C2', 'C3', 'C4', 'D'];
          individualStages.forEach(stage => {
            const stageValue = componentStages[stage] || componentStages[stage.toLowerCase()] || 0;
            if (stageValue !== 0) {
              allStageValues.push(stageValue);
              console.log(`${stage} value for ${component.name}: ${stageValue}`);
            }
          });
        } else {
          // Grouped stages mode - collect grouped stage values
          if (componentStages['A1'] || componentStages['A2'] || componentStages['A3']) {
            const a1a3Value = (componentStages['A1'] || 0) + (componentStages['A2'] || 0) + (componentStages['A3'] || 0);
            allStageValues.push(a1a3Value);
            console.log(`A1-A3 value for ${component.name}: ${a1a3Value}`);
          }
          if (componentStages['A4'] || componentStages['A5']) {
            const a4a5Value = (componentStages['A4'] || 0) + (componentStages['A5'] || 0);
            allStageValues.push(a4a5Value);
            console.log(`A4-A5 value for ${component.name}: ${a4a5Value}`);
          }
          if (componentStages['B1'] || componentStages['B2'] || componentStages['B3'] || componentStages['B4'] || componentStages['B5']) {
            const b1b5Value = (componentStages['B1'] || 0) + (componentStages['B2'] || 0) + (componentStages['B3'] || 0) + 
                              (componentStages['B4'] || 0) + (componentStages['B5'] || 0);
            allStageValues.push(b1b5Value);
            console.log(`B1-B5 value for ${component.name}: ${b1b5Value}`);
          }
          if (componentStages['B6'] || componentStages['B7']) {
            const b6b7Value = (componentStages['B6'] || 0) + (componentStages['B7'] || 0);
            allStageValues.push(b6b7Value);
            console.log(`B6-B7 value for ${component.name}: ${b6b7Value}`);
          }
          if (componentStages['C1'] || componentStages['C2'] || componentStages['C3'] || componentStages['C4']) {
            const c1c4Value = (componentStages['C1'] || 0) + (componentStages['C2'] || 0) + (componentStages['C3'] || 0) + (componentStages['C4'] || 0);
            allStageValues.push(c1c4Value);
            console.log(`C1-C4 value for ${component.name}: ${c1c4Value}`);
          }
          if (componentStages['D'] || componentStages['d']) {
            const dValue = componentStages['D'] || componentStages['d'] || 0;
            allStageValues.push(dValue); // Keep original value (can be negative)
            console.log(`D value for ${component.name}: ${dValue}`);
          }
        }
      });
      
      // For max value calculation, we need to consider both positive and negative values
      // Use absolute values to find the range, but keep original values for display
      const maxPossibleValue = Math.max(...allStageValues.map(v => Math.abs(v)), 1); // Ensure it's at least 1 to avoid division by zero
      
      console.log('MatrixHeatmap - All stage values:', allStageValues);
      console.log('MatrixHeatmap - Max possible value:', maxPossibleValue);

      return components.map(component => {
        const componentStages = lcaData.lcaStagesData[component.id] || {};
        
        console.log(`Processing component: ${component.name} (ID: ${component.id})`);
        console.log(`Component stages data:`, componentStages);
        
        return {
          component: component.name,
          category: component.category,
          stages: lifeCycleStages.map((stage) => {
            let value = 0;
            
            if (expandMap) {
              // Individual stages mode - use individual stage values directly
              const stageCode = stage.id.toUpperCase();
              value = componentStages[stageCode] || componentStages[stageCode.toLowerCase()] || 0;
              console.log(`  ${stageCode}: ${value}`);
            } else {
              // Grouped stages mode - sum grouped stage values
              if (stage.id === 'a1-a3') {
                // Sum A1, A2, A3 values
                value = (componentStages['A1'] || 0) + (componentStages['A2'] || 0) + (componentStages['A3'] || 0);
                console.log(`  A1-A3: A1=${componentStages['A1'] || 0}, A2=${componentStages['A2'] || 0}, A3=${componentStages['A3'] || 0}, Total=${value}`);
              } else if (stage.id === 'a4-a5') {
                // Sum A4, A5 values
                value = (componentStages['A4'] || 0) + (componentStages['A5'] || 0);
                console.log(`  A4-A5: A4=${componentStages['A4'] || 0}, A5=${componentStages['A5'] || 0}, Total=${value}`);
              } else if (stage.id === 'b1-b5') {
                // Sum B1, B2, B3, B4, B5 values
                value = (componentStages['B1'] || 0) + (componentStages['B2'] || 0) + (componentStages['B3'] || 0) + 
                        (componentStages['B4'] || 0) + (componentStages['B5'] || 0);
                console.log(`  B1-B5: B1=${componentStages['B1'] || 0}, B2=${componentStages['B2'] || 0}, B3=${componentStages['B3'] || 0}, B4=${componentStages['B4'] || 0}, B5=${componentStages['B5'] || 0}, Total=${value}`);
              } else if (stage.id === 'b6-b7') {
                // Sum B6, B7 values
                value = (componentStages['B6'] || 0) + (componentStages['B7'] || 0);
                console.log(`  B6-B7: B6=${componentStages['B6'] || 0}, B7=${componentStages['B7'] || 0}, Total=${value}`);
              } else if (stage.id === 'c1-c4') {
                // Sum C1, C2, C3, C4 values
                value = (componentStages['C1'] || 0) + (componentStages['C2'] || 0) + (componentStages['C3'] || 0) + (componentStages['C4'] || 0);
                console.log(`  C1-C4: C1=${componentStages['C1'] || 0}, C2=${componentStages['C2'] || 0}, C3=${componentStages['C3'] || 0}, C4=${componentStages['C4'] || 0}, Total=${value}`);
              } else if (stage.id === 'd') {
                // D value (benefits, can be negative)
                // Check both uppercase 'D' and lowercase 'd' keys
                value = componentStages['D'] || componentStages['d'] || 0;
                console.log(`  D stage processing for ${component.name}:`);
                console.log(`    - componentStages keys:`, Object.keys(componentStages));
                console.log(`    - Looking for 'D' key:`, componentStages.hasOwnProperty('D'));
                console.log(`    - Looking for 'd' key:`, componentStages.hasOwnProperty('d'));
                console.log(`    - 'D' value:`, componentStages['D']);
                console.log(`    - 'd' value:`, componentStages['d']);
                console.log(`    - Final calculated value:`, value);
                console.log(`    - Value type:`, typeof value);
                console.log(`    - Is negative:`, value < 0);
                if (value < 0) {
                  console.log(`  D is NEGATIVE: ${value} - will use green color`);
                } else if (value === 0) {
                  console.log(`  D is ZERO: ${value} - check if data exists`);
                } else {
                  console.log(`  D is POSITIVE: ${value} - will use orange color`);
                }
              }
            }
            
            console.log(`  Final value for ${stage.id}: ${value}`);
            
            return {
              stage: stage.id,
              stageName: stage.name,
              value: value,
              maxValue: maxPossibleValue
            };
          })
        };
      });
    } else {
      // Fallback to simulated data (for sample data or when Excel doesn't have LCA stages)
      const maxPossibleValue = Math.max(...components.map(component => {
        const baseValue = component.impacts[selectedImpact] || 0;
        return baseValue * 0.6; // A1-A3 is typically highest
      }));

    return components.map(component => {
      const baseValue = component.impacts[selectedImpact] || 0;
      return {
        component: component.name,
        category: component.category,
        stages: lifeCycleStages.map((stage, index) => {
          // Simulate different values for different life cycle stages
            let value = 0;
            if (expandMap) {
              // Individual stages mode - distribute values more granularly
              const individualMultipliers = [0.25, 0.20, 0.15, 0.10, 0.05, 0.08, 0.07, 0.06, 0.05, 0.04, 0.03, 0.02, 0.01, 0.01, 0.01, 0.01, -0.02];
              value = Math.max(0, baseValue * (individualMultipliers[index] || 0.01));
            } else {
              // Grouped stages mode - use original multipliers
              const groupedMultipliers = [0.6, 0.15, 0.15, 0.05, 0.05, -0.05];
              value = Math.max(0, baseValue * (groupedMultipliers[index] || 0.01));
            }
          return {
            stage: stage.id,
            stageName: stage.name,
            value: value,
              maxValue: maxPossibleValue
          };
        })
      };
    });
    }
    
    if (isRhinoImport) {
      return [];
    }
  }, [selectedImpact, components, lcaData, expandMap, visibleStages]);

  const componentColumnWidth = expandMap ? 180 : 160;
  const stageColumnMinWidth = expandMap ? 110 : 130;
  const gridTemplateColumns = `minmax(${componentColumnWidth}px, ${componentColumnWidth}px) repeat(${lifeCycleStages.length}, minmax(${stageColumnMinWidth}px, 1fr))`;
  const contentMinWidth = componentColumnWidth + lifeCycleStages.length * stageColumnMinWidth;

  useEffect(() => {
    const frame = requestAnimationFrame(() => updateHeatmapScrollIndicator());
    return () => cancelAnimationFrame(frame);
  }, [matrixData, expandMap, visibleStages, gridTemplateColumns, updateHeatmapScrollIndicator]);

  // Auto-hide callout after 5 seconds when button is clicked
  useEffect(() => {
    if (showCallout) {
      const timer = setTimeout(() => {
        setShowCallout(false);
      }, 5000);
      
      return () => clearTimeout(timer);
    }
  }, [showCallout]);
  
  const getIntensityColor = (value, maxValue) => {
    // Handle zero values with white color
    if (value === 0) {
      return 'bg-white'; // White for zero values
    }
    
    // Handle negative values (environmental benefits) with single green color
    if (value < 0) {
      return 'bg-emerald-300'; // Single green color for all negative values
    }
    
    // Handle positive values (environmental impacts) with orange shades
    if (maxValue <= 0) return 'bg-orange-50';
    
    const ratio = value / maxValue;
    
    console.log(`getIntensityColor - value: ${value}, maxValue: ${maxValue}, ratio: ${ratio}`);
    
    if (ratio > 0.9) return 'bg-orange-900';
    if (ratio > 0.8) return 'bg-orange-800';
    if (ratio > 0.7) return 'bg-orange-700';
    if (ratio > 0.6) return 'bg-orange-600';
    if (ratio > 0.5) return 'bg-orange-500';
    if (ratio > 0.4) return 'bg-orange-400';
    if (ratio > 0.3) return 'bg-orange-300';
    if (ratio > 0.2) return 'bg-orange-200';
    if (ratio > 0.1) return 'bg-orange-100';
    return 'bg-orange-50';
  };

  const downloadHeatmap = async () => {
    if (!heatmapRef.current || isDownloading) return;

    setIsDownloading(true);

    try {
      // Convert the heatmap container to canvas
      const container = heatmapRef.current;
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      // Set high resolution (4x for clarity)
      const scale = 4;
      const rect = container.getBoundingClientRect();
      canvas.width = rect.width * scale;
      canvas.height = rect.height * scale;
      ctx.scale(scale, scale);
      
      // Create a temporary container with the same content
      const tempContainer = container.cloneNode(true);
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.top = '0';
      
      // Add export-specific styling for legend text alignment with manual positioning
      const legendItems = tempContainer.querySelectorAll('.export-legend-item');
      legendItems.forEach(item => {
        // Remove the items-end class and force center alignment
        item.classList.remove('items-end');
        item.style.alignItems = 'center';
        item.style.display = 'flex';
        item.style.alignItems = 'center';
        item.style.justifyContent = 'flex-start';
      });
      
      const legendTexts = tempContainer.querySelectorAll('.export-legend-text');
      legendTexts.forEach(text => {
        text.style.lineHeight = '1.2';
        text.style.verticalAlign = 'middle';
        text.style.display = 'inline-block';
        text.style.position = 'relative';
        text.style.top = '0';
        text.style.alignSelf = 'center';
      });
      
      // Manually position legend text with specific pixel values
      const allTexts = tempContainer.querySelectorAll('.export-legend-text');
      allTexts.forEach(text => {
        if (text.textContent.includes('Benefits')) {
          // Position Benefits text to align with the middle of its color box
          text.style.position = 'relative';
          text.style.top = '-8px'; // Move up by 8px to center with the color box
          text.style.alignSelf = 'center';
          text.style.verticalAlign = 'middle';
          
          // Force the parent container to center align
          const parent = text.closest('.export-legend-item');
          if (parent) {
            parent.style.alignItems = 'center';
            parent.classList.remove('items-end');
          }
        } else if (text.textContent.includes('Low') || text.textContent.includes('High')) {
          // Position Low/High text to align with the middle of color boxes
          text.style.position = 'relative';
          text.style.top = '-8px'; // Move up by 8px to center with the color boxes
          text.style.alignSelf = 'center';
          text.style.verticalAlign = 'middle';
        } else if (text.textContent.includes('Impact Intensity:')) {
          // Move the main label up to align with legend items
          text.style.position = 'relative';
          text.style.top = '-8px'; // Move up by 8px to align with legend items
          text.style.alignSelf = 'center';
          text.style.verticalAlign = 'middle';
        }
      });
      
      document.body.appendChild(tempContainer);
      
      // Use html2canvas to capture the heatmap
      const html2canvas = (await import('html2canvas')).default;
      const canvasResult = await html2canvas(tempContainer, {
        scale: scale,
        useCORS: true,
        allowTaint: true,
        backgroundColor: '#ffffff'
      });
      
      // Remove temporary container
      document.body.removeChild(tempContainer);
      
      // Convert to image
      const imgData = canvasResult.toDataURL('image/jpeg', 1.0);
      
      // Create PDF
      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      
      const imgWidth = pdfWidth;
      const imgHeight = (canvasResult.height * imgWidth) / canvasResult.width;
      
              // If image height exceeds page height, create multiple pages
        if (imgHeight > pdfHeight) {
          // Add margins to prevent overflow
          const margin = 8; // 8mm margin on all sides
          const availableWidth = pdfWidth - (2 * margin);
          const availableHeight = pdfHeight - (2 * margin);
          
          // Calculate actual row heights and fit maximum rows per page
          const totalRows = matrixData.length + 1; // +1 for header
          const headerHeight = -90; // Approximate header height
          const dataRowHeight = (canvasResult.height - headerHeight) / matrixData.length;
          
          // Calculate how many pixels we can fit per page
          const maxPixelsPerPage = Math.floor((availableHeight / imgHeight) * canvasResult.height);
          
          // Calculate how many complete rows we can fit per page
          const maxRowsPerPage = Math.floor((maxPixelsPerPage - headerHeight) / dataRowHeight) + 2; // +1 for header
          
          // Calculate optimal page count based on actual rows that fit
          const pageCount = Math.ceil(totalRows / maxRowsPerPage);
          
          console.log(`Total rows: ${totalRows}, Max rows per page: ${maxRowsPerPage}`);
          console.log(`Data row height: ${dataRowHeight}px, Header height: ${headerHeight}px`);
          console.log(`Creating ${pageCount} pages with ${maxRowsPerPage} rows per page`);
          
          // Generate pages with row-aware splitting
          for (let i = 0; i < pageCount; i++) {
            if (i > 0) {
              pdf.addPage();
            }
            
            // Calculate which rows belong on this page
            const startRow = i * maxRowsPerPage;
            const endRow = Math.min(startRow + maxRowsPerPage, totalRows);
            const rowsOnThisPage = endRow - startRow;
            
            // Calculate source coordinates for this page
            let sourceY = 0;
            let sourceHeight = 0;
            
            if (startRow === 0) {
              // First page: header + data rows
              const dataRows = Math.min(maxRowsPerPage - 1, matrixData.length);
              sourceHeight = Math.max(1, headerHeight + (dataRows * dataRowHeight)); // Ensure positive height
            } else {
              // Subsequent pages: only data rows
              const dataRows = Math.min(maxRowsPerPage, matrixData.length - startRow + 1);
              sourceY = Math.max(0, headerHeight + ((startRow - 1) * dataRowHeight)); // Ensure non-negative Y
              sourceHeight = Math.max(1, dataRows * dataRowHeight); // Ensure positive height
            }
            
            const tempCanvas = document.createElement('canvas');
            tempCanvas.width = canvasResult.width;
            tempCanvas.height = Math.max(1, sourceHeight); // Ensure positive height
            
            // Validate source coordinates to prevent errors
            if (sourceY < 0 || sourceHeight <= 0 || sourceY + sourceHeight > canvasResult.height) {
              console.warn(`Invalid coordinates: sourceY=${sourceY}, sourceHeight=${sourceHeight}, canvasHeight=${canvasResult.height}`);
              continue; // Skip this page if coordinates are invalid
            }
            
            const tempCtx = tempCanvas.getContext('2d');
            tempCtx.drawImage(canvasResult, 0, sourceY, canvasResult.width, sourceHeight, 0, 0, canvasResult.width, sourceHeight);
            
            const tempImgData = tempCanvas.toDataURL('image/jpeg', 1.0);
            
            // Calculate dimensions to fit within margins while maintaining aspect ratio
            const tempImgWidth = availableWidth;
            const tempImgHeight = (sourceHeight * tempImgWidth) / canvasResult.width;
            
            // Push the table to the top of the page to utilize all available space
            const x = margin;
            const y = margin; // Start from top margin, don't center
            
            pdf.addImage(tempImgData, 'JPEG', x, y, tempImgWidth, tempImgHeight);
            
            console.log(`Page ${i + 1}: Rows ${startRow} to ${endRow - 1}, Height: ${sourceHeight}px`);
          }
      } else {
        // Single page - add margins and center the image
        const margin = 8; // 8mm margin on all sides (reduced from 10mm to fit more content)
        const availableWidth = pdfWidth - (2 * margin);
        const availableHeight = pdfHeight - (2 * margin);
        
        // Calculate dimensions to fit within margins while maintaining aspect ratio
        const finalImgWidth = Math.min(imgWidth, availableWidth);
        const finalImgHeight = (canvasResult.height * finalImgWidth) / canvasResult.width;
        
        // Push the table to the top of the page to utilize all available space
        const x = margin + (availableWidth - finalImgWidth) / 2;
        const y = margin; // Start from top margin, don't center
        
        pdf.addImage(imgData, 'JPEG', x, y, finalImgWidth, finalImgHeight);
      }
      
      // Generate filename
      const timestamp = new Date().toISOString().slice(0, 19).replace(/:/g, '-');
      const filename = `heatmap_${selectedImpact}_${timestamp}.pdf`;
      
      pdf.save(filename);
      
      console.log('Heatmap downloaded successfully');
    } catch (error) {
      console.error('Error downloading heatmap:', error);
      alert('Failed to download heatmap. Please try again.');
    } finally {
      setIsDownloading(false);
    }
  };
  
  const selectedImpactInfo = dataSource.impactCategories.find(cat => cat.id === selectedImpact);
  return (
    <Card className="bg-white/80 backdrop-blur-sm border-0 shadow-xl relative">
      <CardHeader>
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="flex flex-1 items-center justify-center gap-2 text-center text-gray-800 sm:justify-start sm:text-left">
            <Grid3X3 className="w-5 h-5 text-emerald-600" />
            Component Impact Heatmap
          </CardTitle>
          <div className="grid w-full grid-cols-1 gap-3 sm:w-auto sm:flex sm:flex-wrap sm:items-center sm:space-x-3 sm:gap-0">
            {/* Expand Map Toggle - Only show when Excel data is available */}
            {lcaData && lcaData.lcaStagesData && Object.keys(lcaData.lcaStagesData).length > 0 && (
              <button
                onClick={() => setExpandMap(!expandMap)}
                className={`w-full px-3 py-1.5 text-xs font-medium rounded-md transition-colors sm:w-auto ${
                  expandMap 
                    ? 'bg-blue-600 text-white hover:bg-blue-700' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {expandMap ? 'Collapse Map' : 'Expand Map'}
              </button>
            )}
            
            {/* Stage Visibility Dropdown - Only show when expanded */}
            {expandMap && (
              <div className="relative sm:w-auto">
                <button
                  onClick={() => document.getElementById('stageDropdown').classList.toggle('hidden')}
                  className="w-full px-3 py-1.5 text-xs font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-md transition-colors flex items-center justify-between sm:w-auto sm:justify-start"
                >
                  <span>Visibility of LCA Stages</span>
                  <svg className="w-3 h-3 ml-1 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                
                <div id="stageDropdown" className="hidden absolute right-0 mt-2 w-64 bg-white border border-gray-200 rounded-md shadow-lg z-10 max-h-60 overflow-y-auto">
                  <div className="p-3">
                    <div className="text-xs font-medium text-gray-700 mb-2">Select stages to show:</div>
                    {individualLifeCycleStages.map(stage => (
                      <label key={stage.id} className="flex items-center space-x-2 py-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={visibleStages.has(stage.id)}
                          onChange={(e) => {
                            const newVisibleStages = new Set(visibleStages);
                            if (e.target.checked) {
                              newVisibleStages.add(stage.id);
                            } else {
                              newVisibleStages.delete(stage.id);
                            }
                            setVisibleStages(newVisibleStages);
                          }}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-xs text-gray-700">
                          {stage.name}: {stage.description}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
            
            {/* Show Values Toggle */}
            <div className="flex items-center space-x-2">
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={!hideValues}
                  onChange={(e) => setHideValues(!e.target.checked)}
                  className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                />
                <span className="text-xs font-medium text-gray-700">Show values</span>
              </label>
            </div>
            
            {/* Download Chart Button */}
            <div className="relative">
              {/* Callout message when expanded map is active */}
              {expandMap && showCallout && (
                <div className="absolute bottom-full right-0 mb-2 px-4 py-3 bg-emerald-50 border border-emerald-200 rounded-md shadow-sm text-xs text-emerald-700 w-80">
                  <div className="flex items-start justify-between">
                    <div className="flex items-start space-x-2 flex-1">
                      {/* Caution symbol */}
                      <svg className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <div className="text-emerald-700 text-justify">
                        Download option is not currently available for 'Expanded Map', toggle back to 'Collapse map' to export
                      </div>
                    </div>
                    {/* Close button */}
                    <button
                      onClick={() => setShowCallout(false)}
                      className="ml-2 text-emerald-400 hover:text-emerald-600 transition-colors flex-shrink-0"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                  {/* Arrow pointing down to button */}
                  <div className="absolute top-full right-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-transparent border-t-emerald-50"></div>
                </div>
              )}
              
              <Button
                variant="outline"
                size="sm"
                onClick={expandMap ? () => setShowCallout(true) : downloadHeatmap}
                disabled={isDownloading}
                className="w-full border-emerald-200 text-emerald-700 hover:bg-emerald-50 hover:border-emerald-300 disabled:opacity-50 disabled:cursor-not-allowed sm:w-auto"
              >
                {isDownloading ? (
                  <div className="w-3 h-3 mr-2 border-2 border-emerald-200 border-t-emerald-600 rounded-full animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
                {isDownloading ? 'Generating...' : 'Download Chart'}
              </Button>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <div
            ref={heatmapScrollContainerRef}
            className="overflow-x-auto md:overflow-visible hide-scrollbar"
            data-mobile-scroll
            style={{ WebkitOverflowScrolling: 'touch' }}
          >
            <div className="md:min-w-0" style={{ minWidth: `${contentMinWidth}px` }}>
              <div
                ref={heatmapRef}
                className="space-y-3 pb-2"
                style={{ width: '100%' }}
              >
                {/* Stage Headers */}
                <div className="grid gap-0.5 mb-3 pr-4" style={{ gridTemplateColumns }}>
                  <div className="text-sm font-medium text-gray-600 text-left">Component</div>
                  {lifeCycleStages.map(stage => (
                    <div key={stage.id} className="text-center px-1">
                      <div
                        className="text-sm font-medium text-gray-800 cursor-help whitespace-nowrap"
                        title={expandMap ? stage.description : undefined}
                      >
                        {stage.name}
                      </div>
                      {!expandMap && (
                        <div className="text-xs text-gray-500 leading-tight">{stage.description}</div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Matrix Grid */}
                <div className="space-y-0.5">
                  {matrixData.map(row => (
                    <div
                      key={row.component}
                      className="grid gap-0.5 items-center pr-4"
                      style={{ gridTemplateColumns }}
                    >
                      {/* Component Name */}
                      <div className="text-xs font-medium text-gray-700 pr-2">
                        <div className="whitespace-nowrap">{row.component}</div>
                        <div className="text-[10px] text-gray-500 whitespace-nowrap">{row.category}</div>
                      </div>

                      {/* Stage Cells */}
                      {row.stages.map(cell => (
                        <div
                          key={`${row.component}-${cell.stage}`}
                          className={`relative h-10 ${getIntensityColor(cell.value, cell.maxValue)} ${
                            cell.value === 0
                              ? 'border border-orange-100'
                              : 'border border-white/20'
                          }`}
                        >
                          <div className="absolute inset-0 flex items-center justify-center">
                            {!hideValues && (
                              <div className="text-[10px] font-medium text-gray-800 text-center px-1">
                                {cell.value.toFixed(0)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ))}
                </div>

                {/* Intensity Legend */}
                <div className="flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-6 mt-6 pt-4 border-t border-gray-200 pr-4">
                  <span className="text-sm font-medium text-gray-600 export-legend-text whitespace-nowrap">Impact Intensity:</span>

                  <div className="flex items-center gap-4 sm:gap-6">
                    {/* Positive Values (Impacts) - Orange */}
                    <div className="flex items-center space-x-2 export-legend-item">
                      <span className="text-xs text-gray-500 export-legend-text">Low</span>
                      <div className="flex space-x-1">
                        {['bg-orange-50', 'bg-orange-100', 'bg-orange-200', 'bg-orange-300', 'bg-orange-400', 'bg-orange-500', 'bg-orange-600', 'bg-orange-700', 'bg-orange-800', 'bg-orange-900'].map((color, index) => (
                          <div key={index} className={`w-3 h-4 ${color}`}></div>
                        ))}
                      </div>
                      <span className="text-xs text-gray-500 export-legend-text">High</span>
                    </div>

                    {/* Negative Values (Benefits) - Green - Moved to last */}
                    <div className="flex items-center space-x-2 export-legend-item">
                      <span className="text-xs text-gray-500 export-legend-text">Benefits</span>
                      <div className="w-3 h-4 bg-emerald-300"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <ScrollIndicator indicator={heatmapScrollIndicator} className="mt-3 h-1" />

          {/* PDF Generation Overlay - shows during download */}
          {isDownloading && (
            <div className="absolute inset-0 bg-white/95 backdrop-blur-md z-40 flex flex-col items-center justify-center rounded-lg border-2 border-emerald-200 shadow-xl">
              {/* Industrial Carbon Process Visualization */}
              <div className="relative mb-8">
                {/* Main Industrial Container */}
                <div className="w-24 h-20 bg-gradient-to-b from-emerald-100 to-emerald-200 rounded-lg border-2 border-emerald-300 shadow-lg relative overflow-hidden">
                  {/* Carbon Emission Flow Lines */}
                  <div className="absolute inset-0">
                    {/* Horizontal flow lines */}
                    <div className="absolute top-2 left-0 w-full h-0.5 bg-emerald-400 animate-pulse"></div>
                    <div className="absolute top-4 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{ animationDelay: '0.3s' }}></div>
                    <div className="absolute top-6 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{ animationDelay: '0.6s' }}></div>
                    <div className="absolute top-8 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{ animationDelay: '0.9s' }}></div>
                    <div className="absolute top-10 left-0 w-full h-0.5 bg-emerald-400 animate-pulse" style={{ animationDelay: '1.2s' }}></div>
                  </div>
                  
                  {/* Vertical processing columns */}
                  <div className="absolute left-2 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse"></div>
                  <div className="absolute left-6 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                  <div className="absolute left-10 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '0.8s' }}></div>
                  <div className="absolute left-14 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '1.2s' }}></div>
                  <div className="absolute left-18 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '1.6s' }}></div>
                  <div className="absolute left-22 top-1 w-1 h-16 bg-emerald-500 rounded-full animate-pulse" style={{ animationDelay: '2s' }}></div>
                  
                  {/* Carbon particles flowing through */}
                  <div className="absolute top-1 left-1 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
                  <div className="absolute top-3 left-5 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                  <div className="absolute top-5 left-9 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
                  <div className="absolute top-7 left-13 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: '0.6s' }}></div>
                  <div className="absolute top-9 left-17 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: '0.8s' }}></div>
                  <div className="absolute top-11 left-21 w-1 h-1 bg-emerald-600 rounded-full animate-bounce" style={{ animationDelay: '1s' }}></div>
                </div>
                
                {/* Industrial Pipes and Connections */}
                <div className="absolute -top-2 left-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse"></div>
                <div className="absolute -top-2 right-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse" style={{ animationDelay: '0.5s' }}></div>
                <div className="absolute -bottom-2 left-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse" style={{ animationDelay: '1s' }}></div>
                <div className="absolute -bottom-2 right-4 w-16 h-1 bg-emerald-400 rounded-full animate-pulse" style={{ animationDelay: '1.5s' }}></div>
                
                {/* Connection Nodes */}
                <div className="absolute -top-2 left-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping"></div>
                <div className="absolute -top-2 right-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping" style={{ animationDelay: '0.5s' }}></div>
                <div className="absolute -bottom-2 left-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
                <div className="absolute -bottom-2 right-4 w-2 h-2 bg-emerald-600 rounded-full animate-ping" style={{ animationDelay: '1.5s' }}></div>
                
                {/* Carbon Emission Clouds */}
                <div className="absolute -top-4 left-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse"></div>
                <div className="absolute -top-4 right-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse" style={{ animationDelay: '0.7s' }}></div>
                <div className="absolute -bottom-4 left-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse" style={{ animationDelay: '1.4s' }}></div>
                <div className="absolute -bottom-4 right-8 w-3 h-2 bg-emerald-300 rounded-full opacity-60 animate-pulse" style={{ animationDelay: '2.1s' }}></div>
              </div>
              
              {/* Industrial Process Text */}
              <div className="text-xl font-semibold text-emerald-700 mb-3 animate-pulse">
                Processing Heatmap Data
              </div>
              
              {/* Process Status Indicators */}
              <div className="flex space-x-3 mb-4">
                <div className="flex flex-col items-center">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping"></div>
                  <div className="text-xs text-emerald-600 mt-1">Collecting</div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" style={{ animationDelay: '0.3s' }}></div>
                  <div className="text-xs text-emerald-600 mt-1">Analyzing</div>
                </div>
                <div className="flex flex-col items-center">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full animate-ping" style={{ animationDelay: '0.6s' }}></div>
                  <div className="text-xs text-emerald-600 mt-1">Generating</div>
                </div>
              </div>
              
              {/* Progress Bar */}
              <div className="w-48 bg-emerald-100 rounded-full h-2">
                <div className="bg-emerald-500 h-2 rounded-full animate-pulse" style={{ width: '100%' }}></div>
              </div>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MatrixHeatmap;
